/**
 * @module constants/env
 * @description Environment detection and configuration utilities.
 * Provides runtime and build-time environment checks for the UFE application.
 * Centralizes environment detection logic for production, QA, and local environments.
 *
 * @requires constants/constants - Imports environment constant values
 */

// manage the environment
// centralize the location of the checks for prod / qa / localStorage
import { NODE_ENV_PRODUCTION, NODE_ENV_DEVELOPMENT, UFE_ENV_PRODUCTION, UFE_ENV_QA, UFE_ENV_LOCAL } from 'constants/constants';

const ufeEnv = global.process.env.UFE_ENV;
const nodeEnv = global.process.env.NODE_ENV;

/**
 * @constant {boolean} isNodeEnvProduction - Build-time production environment check
 * @description True when NODE_ENV is set to 'production' at build time.
 * This is a build-time constant that doesn't change after compilation.
 * React and other libraries use this to strip out development code.
 */
const isNodeEnvProduction = nodeEnv === NODE_ENV_PRODUCTION;

/**
 * @constant {boolean} isNodeEnvDevelopment - Build-time development environment check
 * @description True when NODE_ENV is set to 'development' at build time.
 * This is a build-time constant used for development builds.
 */
const isNodeEnvDevelopment = nodeEnv === NODE_ENV_DEVELOPMENT;

/**
 * @constant {boolean} isUfeEnvProduction - Runtime production environment check
 * @description True when UFE_ENV is set to 'PROD' at runtime.
 * Used for analytics and determining production vs. test API endpoints.
 */
const isUfeEnvProduction = ufeEnv === UFE_ENV_PRODUCTION;

/**
 * @constant {boolean} isUfeEnvQA - Runtime QA environment check
 * @description True when UFE_ENV is set to 'QA' at runtime.
 * Used to enable QA-specific features and API endpoints.
 */
const isUfeEnvQA = ufeEnv === UFE_ENV_QA;

/**
 * @constant {boolean} isUfeEnvLocal - Runtime local environment check
 * @description True when UFE_ENV is set to 'LOCAL' at runtime.
 * Used for local development environment configuration.
 */
const isUfeEnvLocal = ufeEnv === UFE_ENV_LOCAL;

/**
 * Determines the current runtime environment based on image host
 * @function getCurrentEnv
 * @returns {string|undefined} Environment identifier ('production', 'qa3', 'qa4') or undefined if not recognized
 * @description Inspects Sephora.imageHost to determine the specific environment.
 * Logs a warning if the environment cannot be determined from the host.
 *
 * @example
 * const env = getCurrentEnv();
 * if (env === 'production') {
 *   // Use production settings
 * }
 */
const getCurrentEnv = function () {
    const currentHost = Sephora.imageHost;
    let currentEnv;

    if (isUfeEnvProduction) {
        currentEnv = 'production';
    } else if (isUfeEnvQA || isUfeEnvLocal) {
        if (currentHost?.includes('qa3')) {
            currentEnv = 'qa3';
        } else if (currentHost?.includes('qa4')) {
            currentEnv = 'qa4';
        }
    }

    if (!currentEnv) {
        // eslint-disable-next-line no-console
        console.warn(`The env was not recognized by the host: [${currentHost}]`);
    }

    return currentEnv;
};

export { isUfeEnvProduction, isUfeEnvQA, isUfeEnvLocal, isNodeEnvProduction, isNodeEnvDevelopment, getCurrentEnv };
